import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { User, MapPin, Phone, Mail } from 'lucide-react';
import { Badge } from './ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VotreInterlocuteur = ({ operation }) => {
  const [operator, setOperator] = useState(null);
  const [fieldOps, setFieldOps] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (operation) {
      fetchResponsables();
    }
  }, [operation]);

  const fetchResponsables = async () => {
    try {
      // Get operator details if assigned — une personne hors équipe opérationnelle reste interne
      if (operation.assignedOperatorId && !operation.assignedOperatorIsCrossRole) {
        const opResponse = await axios.get(`${API}/internal-users/${operation.assignedOperatorId}`);
        setOperator(opResponse.data);
      }

      // Get field ops details if assigned
      if (operation.assignedFieldOpsId && !operation.assignedFieldOpsIsCrossRole) {
        const fopsResponse = await axios.get(`${API}/internal-users/${operation.assignedFieldOpsId}`);
        setFieldOps(fopsResponse.data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching responsables:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-[#1db584] shadow-sm">
        <CardContent className="pt-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!operator && !fieldOps) {
    return (
      <Card className="border-[#1db584] shadow-sm">
        <CardHeader className="bg-gradient-to-r from-[#1db584]/5 to-[#1db584]/10 pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-[#1db584]">
            <User className="w-5 h-5" />
            Votre interlocuteur Baobab
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <p className="text-sm text-gray-500 text-center py-4">
            Un opérateur vous sera assigné prochainement pour gérer votre dossier.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#1db584] shadow-sm">
      <CardHeader className="bg-gradient-to-r from-[#1db584]/5 to-[#1db584]/10 pb-3">
        <CardTitle className="text-lg flex items-center gap-2 text-[#1db584]">
          <User className="w-5 h-5" />
          Votre interlocuteur Baobab
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {/* Opérateur en charge */}
        {operator && (
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1db584] to-[#159f6e] flex items-center justify-center text-white text-xl font-bold shadow-md">
                {operator.firstName?.charAt(0)}{operator.lastName?.charAt(0)}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900">
                  {operator.firstName} {operator.lastName}
                </h3>
                <Badge className="bg-[#1db584] text-white text-xs">
                  {operator.jobTitle || 'Votre contact'}
                </Badge>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">
                Responsable de votre dossier
              </p>

              <div className="space-y-2">
                {operator.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="truncate">{operator.email}</span>
                  </div>
                )}
                {operator.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{operator.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Field Ops (si assigné) */}
        {fieldOps && (
          <>
            <div className="border-t pt-6">
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-lg font-bold shadow-md">
                    {fieldOps.firstName?.charAt(0)}{fieldOps.lastName?.charAt(0)}
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 text-sm">
                      {fieldOps.firstName} {fieldOps.lastName}
                    </h3>
                    <Badge className="bg-blue-500 text-white text-xs">
                      <MapPin className="w-3 h-3 mr-1" />
                      {fieldOps.jobTitle || 'Suivi terrain'}
                    </Badge>
                  </div>
                  
                  <p className="text-xs text-gray-600 mb-2">
                    En charge de la coordination terrain
                  </p>

                  {fieldOps.phone && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Phone className="w-3 h-3 text-gray-400" />
                      <span>{fieldOps.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default VotreInterlocuteur;
